import React, { useEffect } from 'react';
import { X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import ModalShell from './ModalShell';
import { haptic } from '../utils/vibration';

/**
 * Модальное окно подтверждения
 * @param {boolean} isOpen - Открыто ли окно
 * @param {string} title - Заголовок
 * @param {string} message - Сообщение
 * @param {string} confirmText - Текст кнопки подтверждения
 * @param {string} cancelText - Текст кнопки отмены
 * @param {string} alternativeText - Текст дополнительной кнопки действия
 * @param {React.ReactNode} confirmIcon - Иконка основной кнопки
 * @param {React.ReactNode} alternativeIcon - Иконка дополнительной кнопки
 * @param {function} onConfirm - Обработчик подтверждения
 * @param {function} onCancel - Обработчик отмены
 * @param {'danger' | 'warning' | 'info'} type - Тип окна
 * @param {Array<{id: number, name: string}>} servicesList - Список услуг для отображения
 */
const ConfirmModal = ({
  isOpen,
  title,
  message,
  confirmText = 'Подтвердить',
  cancelText = 'Отмена',
  alternativeText = '',
  confirmIcon = null,
  alternativeIcon = null,
  onConfirm,
  onCancel,
  onAlternative,
  type = 'danger',
  servicesList = []
}) => {
  useEffect(() => {
    if (isOpen) {
      if (type === 'danger' || type === 'warning') {
        haptic('warning');
      } else {
        haptic('info');
      }
    }
  }, [isOpen, type]);

  const handleCancel = (e) => {
    haptic('close');
    onCancel?.(e);
  };

  const handleConfirm = (e) => {
    haptic(type === 'danger' ? 'medium' : 'selection');
    onConfirm?.(e);
  };

  const handleAlternative = (e) => {
    haptic('selection');
    onAlternative?.(e);
  };

  const typeConfig = {
    danger: {
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100 dark:bg-red-900/20',
      confirmBg: 'bg-red-600 hover:bg-red-700'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-100 dark:bg-orange-900/20',
      confirmBg: 'bg-orange-600 hover:bg-orange-700'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100 dark:bg-blue-900/20',
      confirmBg: 'bg-blue-600 hover:bg-blue-700'
    }
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleCancel}
      variant="sheet"
      zIndexClass="z-[100100]"
      panelClassName="sm:max-w-sm overflow-hidden"
      closeOnOverlay={false}
    >
            {/* Заголовок */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">{title}</h3>
              <button onClick={handleCancel} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Контент */}
            <div className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <div className={`p-2 rounded-xl ${config.iconBg}`}>
                  <Icon className={`w-6 h-6 ${config.iconColor}`} />
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 flex-1">{message}</p>
              </div>

              {/* Список услуг если есть */}
              {servicesList && servicesList.length > 0 && (
                <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800 rounded-xl">
                  <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
                    ⚠️ Услуги, которые останутся без группы:
                  </p>
                  <ul className="max-h-40 overflow-y-auto space-y-1">
                    {servicesList.map(service => (
                      <li key={service.id} className="text-sm text-yellow-700 dark:text-yellow-400 flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full flex-shrink-0"></span>
                        {service.name}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Кнопки */}
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleCancel}
                  className="min-w-[88px] flex-1 px-3 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
                >
                  {cancelText}
                </button>
                {alternativeText && onAlternative && (
                  <button
                    onClick={handleAlternative}
                    className="min-w-[104px] flex-1 px-3 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm"
                  >
                    <span className="inline-flex items-center justify-center gap-1.5">{alternativeIcon}{alternativeText}</span>
                  </button>
                )}
                <button
                  onClick={handleConfirm}
                  className={`min-w-[120px] flex-1 px-3 py-2.5 text-white rounded-xl transition-colors font-medium text-sm ${config.confirmBg}`}
                >
                  <span className="inline-flex items-center justify-center gap-1.5">{confirmIcon}{confirmText}</span>
                </button>
              </div>
            </div>
    </ModalShell>
  );
};

/**
 * Модальное окно информационного сообщения (Alert)
 * @param {boolean} isOpen - Открыто ли окно
 * @param {string} title - Заголовок
 * @param {string} message - Сообщение
 * @param {'success' | 'error' | 'info' | 'warning'} type - Тип сообщения
 * @param {function} onClose - Обработчик закрытия
 */
const AlertModal = ({
  isOpen,
  title,
  message,
  type = 'info',
  onClose
}) => {
  useEffect(() => {
    if (isOpen) {
      if (type === 'error') {
        haptic('error');
      } else if (type === 'success') {
        haptic('success');
      } else if (type === 'warning') {
        haptic('warning');
      } else {
        haptic('info');
      }
    }
  }, [isOpen, type]);

  const handleClose = (e) => {
    haptic('close');
    onClose?.(e);
  };

  const typeConfig = {
    success: {
      icon: CheckCircle,
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100 dark:bg-green-900/20'
    },
    error: {
      icon: AlertTriangle,
      iconColor: 'text-red-600',
      iconBg: 'bg-red-100 dark:bg-red-900/20'
    },
    info: {
      icon: Info,
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100 dark:bg-blue-900/20'
    },
    warning: {
      icon: AlertTriangle,
      iconColor: 'text-orange-600',
      iconBg: 'bg-orange-100 dark:bg-orange-900/20'
    }
  };

  const config = typeConfig[type];
  const Icon = config.icon;

  return (
    <ModalShell
      open={isOpen}
      onClose={handleClose}
      variant="sheet"
      zIndexClass="z-[100100]"
      panelClassName="sm:max-w-sm overflow-hidden"
      closeOnOverlay={false}
    >
            {/* Контент */}
            <div className="p-4">
              <div className="flex items-start gap-3 mb-4">
                <div className={`p-2 rounded-xl ${config.iconBg} flex-shrink-0`}>
                  <Icon className={`w-8 h-8 ${config.iconColor}`} />
                </div>
                <div className="flex-1">
                  {title && (
                    <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 mb-1">{title}</h3>
                  )}
                  <p className="text-sm text-gray-700 dark:text-gray-300">{message}</p>
                </div>
              </div>

              {/* Кнопка закрытия */}
              <button
                onClick={handleClose}
                className="w-full px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
              >
                Закрыть
              </button>
            </div>
    </ModalShell>
  );
};

export default ConfirmModal;
export { ConfirmModal, AlertModal };
